import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconMouse,
  IconChevronsDown,
  IconCalendarEvent,
  IconInfinity,
  IconCircleCheck,
  IconSparkles,
} from "@tabler/icons-react";

// Material UI imports
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid2,
  List,
  ListItem,
  Typography,
  CircularProgress,
} from "@mui/material";

// Project imports
import Header from "../../ui-components/header";
import Footer from "../../ui-components/footer";
import PopUpModal from "../../ui-components/PopUpModal";
import ChangeDetailsContent from "../changeDetails/ChangeDetailsContent";
import HeroImage from "../../assets/suwaarana-cover-scaled.webp";
import PackageServices from "../../services/v1/packageservices";

const packageContent = {
  "Sponsor a Day": {
    icon: IconCalendarEvent,
    summary: "Reserve one date with a single donation.",
    details: [
      {
        title: "One-Time",
        text: "Reserve one date with one donation.",
      },
      {
        title: "Monthly Recurring",
        text: "Reserve the same day every month.",
      },
      {
        title: "Annual Recurring",
        text: "Reserve the same day every year.",
      },
    ],
    buttonLabel: "Select Sponsor a Day",
  },
  "Sponsor Lifetime": {
    icon: IconInfinity,
    summary:
      "Create a legacy of kindness with support that continues for generations.",
    details: [
      {
        title: "Highest Impact",
        text: "Reserve this date annually for the next 100 years.",
      },
      {
        title: "Lifetime Recognition",
        text: "Receive ongoing recognition for your sponsorship.",
        icon: IconSparkles,
      },
    ],
    buttonLabel: "Select Sponsor Lifetime",
  },
};

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sponsorData, setSponsorData] = useState([]);

  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [isChangeDetailsOpen, setIsChangeDetailsOpen] = useState(false);

  // Fetch sponsor data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await PackageServices.getAllPackages();
        setSponsorData(res.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch sponsor data. Please try again.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  // Confirmation pages link back here with ?openChangeDetails=1 so the
  // donor lands straight on the OTP-verified update flow instead of just
  // the homepage.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openChangeDetails")) {
      setIsChangeDetailsOpen(true);
    }
  }, [location.search]);

  const handleSelect = (title, price, packageID) => {
    navigate("/checkout", {
      state: { title, price, packageID, scrollTo: "checkout-herosection" },
    });
  };

  const renderPackageDetails = (title) => packageContent[title] || null;

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundImage: `url(${HeroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: 825,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <Grid2 item size={{ md: 12, xs: 6 }}>
        <Box align="center" justifyContent="center" sx={{ mt: 15 }}>
          <Button
            variant="contained"
            onClick={() => {
              const element = document.getElementById("packages-preview");
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
            }}
            sx={{
              bgcolor: "#8B2757",
              color: "white",
              "&:hover": {
                bgcolor: "transparent",
                color: "#8B2757",
                border: "1px solid #8B2757",
              },
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                },
                "50%": {
                  transform: "scale(1.05)",
                },
                "100%": {
                  transform: "scale(1)",
                },
              },
            }}
          >
            <IconMouse size={20} style={{ marginRight: 8 }} />
            Scroll Down
          </Button>
          <Box sx={{ mt: 2 }}>
            <IconChevronsDown
              size={26}
              color="#8B2757"
              sx={{
                animation: "waveDown 2s infinite ease-in-out",
                "@keyframes waveDown": {
                  "0%, 100%": {
                    transform: "translateY(0)",
                    opacity: 1,
                  },
                  "50%": {
                    transform: "translateY(20px)",
                    opacity: 0.5,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Grid2>
      <Grid2
        container
        spacing={4}
        sx={{ justifyContent: "center", bgcolor: "white", pt: { xs: 5, md: 8 }, pb: 5 }}
      >
        <Grid2 item size={{ xs: 11, md: 5 }} sx={{ px: { xs: 1, md: 4 } }}>
          <Typography
            variant="h3"
            sx={{
              color: "#8B2757",
              fontWeight: "bold",
              textAlign: { xs: "center", md: "left" },
              fontSize: { md: "2.6rem", xs: "2rem" },
            }}
          >
            Select a Package
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 3,
              textAlign: { xs: "center", md: "left" },
              fontSize: { md: 18, xs: 16 },
            }}
          >
            In the midst of life's toughest battles, there is a sanctuary of
            hope and healing - Suwa Arana. For children facing the unimaginable
            challenge of cancer and their families, our haven offers respite,
            strength, and a glimmer of light amidst the darkness. <br />
            <br />
            Reserve YOUR Day by filling in the advance request. A sponsor will
            cover the occupancy of all 32 rooms for children together with their
            families and the total cost of personnel, outsourced services,
            utilities, linen, food and beverages, and consumables.
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ mt: 5, fontSize: 20, textAlign: { xs: "center", md: "left" } }}
          >
            YOU will also have the following day sponsorship benefits:
          </Typography>
          <List
            sx={{
              mt: 2,
              listStyleType: "disc",
              paddingLeft: { xs: "1.5rem", md: "1.5rem" },
              fontSize: { md: 20, xs: 16 },
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              Enjoy a facility visit & spend the day.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Meet and connect with children and families.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Join a relaxation program and recreational activities.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Experience guided meditation and art therapy.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Take a calming walk and attend a yoga session.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Participate in group counseling and volunteering.
            </ListItem>
          </List>
        </Grid2>

        <Grid2
          item
          size={{ xs: 11, md: 5 }}
          sx={{ px: { xs: 1, md: 4 }, mt: { xs: 6, md: 0 } }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#8B2757",
              fontWeight: "bold",
              textAlign: { xs: "center", md: "left" },
              fontSize: { md: "2.6rem", xs: "2rem" },
            }}
          >
            Reserve Your Date Today
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 3,
              textAlign: { xs: "center", md: "left" },
              fontSize: { md: 18, xs: 16 },
            }}
          >
            Already sponsored a day and need to update your details or your
            reserved date? You don't need to contact us -- verify your email
            and phone number and make the change yourself.
          </Typography>
          <Box sx={{ textAlign: { xs: "center", md: "left" }, mt: 3, mb: 5 }}>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#8B2757",
                color: "#8B2757",
                "&:hover": { bgcolor: "#8B2757", color: "white" },
              }}
              onClick={() => setIsChangeDetailsOpen(true)}
            >
              Change Your Details
            </Button>
          </Box>

          <Grid2
            container
            spacing={3}
            id="packages-preview"
            sx={{ justifyContent: { xs: "center", md: "flex-start" } }}
          >
            {loading ? (
              <Box>
                <CircularProgress color="secondary" />
              </Box>
            ) : error ? (
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            ) : (
              sponsorData.map((data, index) => {
                const packageDetails = renderPackageDetails(data.title);

                return (
                  <Grid2 item size={{ xs: 12, sm: 6 }} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        mb: 3,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        textAlign: "center",
                        px: 2,
                        py: 2.5,
                        minHeight: { md: 420, xs: "auto" },
                      }}
                    >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        mb: 2,
                        minHeight: 40,
                      }}
                    >
                      {packageDetails?.icon ? (
                        <Box sx={{ display: "inline-flex" }}>
                          {React.createElement(packageDetails.icon, {
                            size: 28,
                            color: "#8B2757",
                          })}
                        </Box>
                      ) : null}
                      <Typography
                        sx={{
                          color: "#8B2757",
                          fontSize: 16,
                          fontWeight: "bold",
                          lineHeight: 1.2,
                          textAlign: "center",
                        }}
                      >
                        {data.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 15,
                        mb: 2,
                        minHeight: { md: 72, xs: 0 },
                        textAlign: "center",
                      }}
                    >
                      {packageDetails?.summary || data.description}
                    </Typography>
                    {packageDetails?.details?.map((detail) => {
                      const DetailIcon = detail.icon || IconCircleCheck;

                      return (
                        <Box
                          key={detail.title}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.25,
                            mb: 1.5,
                            textAlign: "left",
                          }}
                        >
                          <DetailIcon size={20} color="#8B2757" />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold", lineHeight: 1.3 }}
                            >
                              {detail.title}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 14 }}>
                              {detail.text}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 18,
                        fontWeight: "bold",
                        mt: "auto",
                        py: 3,
                        textAlign: "center",
                      }}
                    >
                      Rs.{" "}
                      {new Intl.NumberFormat("en-US", {
                        style: "decimal",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(data.price)}
                    </Typography>
                  </CardContent>
                  <CardActions
                    sx={{ mt: "auto", justifyContent: "center", pb: 3 }}
                  >
                    <Button
                      sx={{
                        backgroundColor: "#8B2757",
                        borderRadius: 5,
                      }}
                      variant="contained"
                      onClick={() =>
                        handleSelect(data.title, data.price, data.packageID)
                      }
                    >
                      {packageDetails?.buttonLabel || "Select"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid2>
                );
              })
            )}
          </Grid2>
        </Grid2>
      </Grid2>
      <Box sx={{ mt: 10 }}>
        <Footer />
      </Box>
      <PopUpModal
        open={isChangeDetailsOpen}
        handleCloseDialog={() => setIsChangeDetailsOpen(false)}
        title="Change Your Details"
        content={
          <ChangeDetailsContent
            handleCloseDialog={() => setIsChangeDetailsOpen(false)}
          />
        }
        maxWidth={500}
        showCloseIcon={true}
      />
    </Box>
  );
};

export default Index;
